import React, { useState, useCallback, createContext, useContext } from 'react';
import { AlertTriangle, X } from 'lucide-react';

const ConfirmContext = createContext(null);

/**
 * Reusable confirm dialog that replaces native window.confirm().
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title: 'Ban User?', message: '...' });
 *   if (ok) { ... }
 */
export function ConfirmProvider({ children }) {
    const [state, setState] = useState(null); // { title, message, confirmLabel, danger, resolve }

    const confirm = useCallback(({ title, message, confirmLabel = 'Confirm', danger = false } = {}) => {
        return new Promise((resolve) => {
            setState({ title, message, confirmLabel, danger, resolve });
        });
    }, []);

    const handleConfirm = () => {
        state?.resolve(true);
        setState(null);
    };

    const handleCancel = () => {
        state?.resolve(false);
        setState(null);
    };

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            {state && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[90] animate-fade-in" onClick={handleCancel}>
                    <div
                        className="bg-gradient-to-b from-slate-800 to-slate-900 border border-white/[0.08] rounded-3xl p-6 w-full max-w-sm mx-4 shadow-2xl shadow-black/50 animate-scale-in"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-start space-x-3 mb-4">
                            <div className={`p-2.5 rounded-xl shrink-0 ${state.danger ? 'bg-red-500/10' : 'bg-blue-500/10'}`}>
                                <AlertTriangle size={18} className={state.danger ? 'text-red-400' : 'text-blue-400'} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="text-lg font-extrabold text-white">{state.title || 'Confirm Action'}</h3>
                                <p className="text-sm text-slate-400 mt-1 leading-relaxed">{state.message || 'Are you sure you want to proceed?'}</p>
                            </div>
                            <button onClick={handleCancel} className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-white/[0.04] transition-all shrink-0">
                                <X size={16} />
                            </button>
                        </div>
                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={handleCancel}
                                className="px-4 py-2.5 text-sm text-slate-400 hover:text-white bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] rounded-xl transition-all font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirm}
                                className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 ${state.danger
                                    ? 'bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white shadow-lg shadow-red-600/25'
                                    : 'bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white shadow-lg shadow-blue-600/25'
                                    }`}
                            >
                                {state.confirmLabel}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </ConfirmContext.Provider>
    );
}

export function useConfirm() {
    const ctx = useContext(ConfirmContext);
    if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider');
    return ctx;
}
